import TextLimit from "Api/TextLimit";
import React, { useEffect, useState } from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getHeaderTooltip } from "../tooltips";

import IconCell from "./IconCell";
import { ReactComponent as MessageIcon } from "../../../../../assets/utils/images/svgs/Message.svg";
import { ReactComponent as SearchIcon } from "../../../../../assets/utils/images/svgs/search.svg";
import { ReactComponent as BookIcon } from "../../../../../assets/utils/images/svgs/Book-icon.svg";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable, flexRender, } from "@tanstack/react-table";
import ColumnHeader from "./ColumnHeader";
import { allOptions, generateKmOptions, generateMatchOptions, getNextOption, limitedOptions, } from "../helpers";
import SelectColumnHeader from "./SelectColumnHeader";
import MessagesCell from "./MessagesCell";
import SelectCell from "./SelectCell";
import { Button } from "reactstrap";
import AvailabilityHeader from "./AvailabilityHeader";
import AvailabilityCell from "./AvailabilityCell";
import PlanscreenService from "Api/Verzoeken/Planscreen";
import {
  getCachedFiltersForContract,
  getCommunityContractKey,
  setCachedFiltersForContract,
} from "helperFunctions/planscreenFiltersCache";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ContractNameHeader from "./ContractNameHeader";
import ContactHistoryCell from "./ContactHistoryCell";
import KmCell from "./KmCell";
import NameCell from "./NameCell";
import FvFbHeader from "./FvFbHeader";
import { Loader } from "react-bootstrap-typeahead";
import ClassicCell from "./ClassicCell";
import KrCell from "./KrCell";
import useSWR from "swr";

const allOptionsHeaders = ["FV", "FB"];
const limitedOptionsHeaders = ["Gym", "V", "I", "P", "36", "Kr", "Bk"];
const allHeaders = [...allOptionsHeaders, ...limitedOptionsHeaders];

export default function Table({
  details,
  contract,
  shiftId,
  sortBy,
  toggleContractVisibility,
  usersToNotify,
  setUsersToNotify,
  setIsOpenMessageModal,
  excludeWwz,
  communityId = null,
  assignedUsers,
  setAssignedUsers,
  allContracts = [],
  contractFilters,
  onEmptyChange,
  preloadedFilters,
  registerContractView,
  contractViews
}) {

  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState([]);
  const [availabilityType, setAvailabilityType] = useState("dagdelen");
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);
  const [lockFilters, setLockFilters] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [headerStates, setHeaderStates] = useState(() =>
    Object.fromEntries(allHeaders.map((h) => [h, "N"]))
  );
  const [filterSortBy, setFilterSortBy] = useState(sortBy);
  const queryClient = useQueryClient();

  const normalizedCommunityId =
    communityId !== null && communityId !== undefined
      ? String(communityId).trim()
      : null;
  const isIngepland = contract.id < 0;
  const filterContractId = isIngepland
    ? allContracts.find((c) => c.id > 0)?.id
    : contract.id;
  const normalizedContractId =
    filterContractId > 0 ? String(filterContractId).trim() : null;

  const communityContractKey =
    normalizedCommunityId && normalizedContractId
      ? getCommunityContractKey(normalizedCommunityId, normalizedContractId)
      : null;
  const queryKey = [
    "planscreen",
    "filters",
    normalizedCommunityId || "0",
    normalizedContractId || "0",
  ];

  const cachedQuery = communityContractKey
    ? queryClient.getQueryData(queryKey)
    : null;

  const cachedFilters = communityContractKey
    ? getCachedFiltersForContract(
      normalizedCommunityId,
      normalizedContractId
    )
    : null;
  const initialFilters = Array.isArray(preloadedFilters)
    ? preloadedFilters
    : Array.isArray(cachedQuery)
      ? cachedQuery
      : Array.isArray(cachedFilters)
        ? cachedFilters
        : undefined;

  const fetchFilters = async () => {
    if (!normalizedContractId) return [];
    if (!normalizedCommunityId) return [];

    try {
      const res = await PlanscreenService.getFilters(
        normalizedContractId,
        excludeWwz,
        normalizedCommunityId
      );

      let filtersData = null;

      if (Array.isArray(res)) {
        filtersData = res;
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        filtersData = res.data.data;
      } else if (Array.isArray(res?.data)) {
        filtersData = res.data;
      }

      if (!filtersData) return [];

      return normalizeFilters(filtersData);
    } catch (e) {
      return [];
    }
  };

  const {
    data: filtersData,
    error: filtersError,
    isLoading: isFiltersLoading,
  } = useSWR(
    normalizedContractId && normalizedCommunityId
      ? queryKey
      : null,
    fetchFilters,
    {
      fallbackData: initialFilters,
      revalidateOnFocus: false,
      dedupingInterval: 30 * 60 * 1000,
    }
  );

  const normalizeFilters = (filtersData) => {
    if (!Array.isArray(filtersData)) return [];
    const validFilters = filtersData.filter(
      (f) => f && (f.id !== undefined || f.name !== undefined)
    );
    return validFilters.map((f, i) => {
      return { ...f, isSelected: i === 0 };
    });
  };


  // useEffect(() => {
  //   if (filtersData) {
  //     setFilters(filtersData);
  //   }
  // }, [filtersData]);

  const cycleHeaderValue = (header, options, id) => {
    try {
      const current = columnFilters.find((f) => f.id === id)?.value ?? "N";
      const nextValue = getNextOption(current, options);
      if (!nextValue) throw new Error("Invalid filter option");
      handleFiltering(id, nextValue === "disabled" || nextValue === "N" ? "" : nextValue);
    } catch (e) {
      console.error("Error in cycleHeaderValue", e);
      handleFiltering(id, "");
    }
  };

  const handleFiltering = (id, value) => {
    // if (hiddenColumns.find((item) => item.id === id)) return;
    if (id !== "availability" && hiddenColumns.find((item) => item.id === id)) return;

    setColumnFilters((prev) => {
      const filtered = prev.filter((f) => f.id !== id);
      if (!value || value === "" || value === "N" || value === "disabled") return [...filtered];
      return [...filtered, { id, value }];
    });
  };

  const resetAllFilters = () => {
    setColumnFilters([]);
    setHeaderStates(() =>
      Object.fromEntries(allHeaders.map((h) => [h, "N"]))
    );
    setIsActiveSelect(false);
    handleUserDataUpdation(0, "selected", false);
    setUsersToNotify([]);
  };

  const renderHeaderCell = (header, id) => {
    let options;
    let handleSelect;
    const isLock = lockFilters.find((f) => f.id === id)?.value ?? false;
    const isHidden = hiddenColumns.find((f) => f.id === id)?.value ?? false;

    switch (header) {
      case "Lft":
        return (
          <Tippy
            content={getHeaderTooltip("Lft")}
            placement="top"
            arrow
            className="custom-tooltip"
          >
            <div>Lft</div>
          </Tippy>
        );

      case "S/V":
        return (
          <Tippy
            content={getHeaderTooltip("S/V")}
            placement="top"
            arrow
            className="custom-tooltip"
          >
            <div>S/V</div>
          </Tippy>
        );
      case "Con":
        return (
          <Tippy
            content={getHeaderTooltip("Con")}
            placement="top"
            arrow
            className="custom-tooltip"
          >
            <div>Con</div>
          </Tippy>
        );
      case "%B":
        return (
          <Tippy
            content={getHeaderTooltip("%B")}
            placement="top"
            arrow
            className="custom-tooltip"
            appendTo={() => document.body}
            delay={[120, 0]}
          >
            <div>%B</div>
          </Tippy>
        );

      case "%E":
        return (
          <Tippy
            content={getHeaderTooltip("%E")}
            placement="top"
            arrow
            className="custom-tooltip"
            appendTo={() => document.body}
            delay={[120, 0]}
          >
            <div>%E</div>
          </Tippy>
        );


      case "Fullname":
        return (
          <ContractNameHeader
            contract={contract}
            users={data}
            usersCount={table?.getFilteredRowModel()?.rows?.length}
            filters={filters}
            setFilters={setFilters}
            toggleContractVisibility={toggleContractVisibility}
            resetAllFilters={resetAllFilters}

          />
        );
      case "FV":
      case "FB":
        handleSelect = (value) => {
          handleFiltering(id, value);
        };
        return (
          <FvFbHeader
            value={columnFilters.find((f) => f.id === id)?.value ?? "N"}
            name={header}
            options={allOptions}
            setValue={handleSelect}
            isLock={isLock}
            isHidden={isHidden}
          />
        );
      case "Gym":
      case "V":
      case "I":
      case "P":
      case "36":
      case "Kr":
      case "Bk":
        return (
          <ColumnHeader
            value={columnFilters.find((f) => f.id === id)?.value ?? "N"}
            name={header}
            onClick={() => cycleHeaderValue(header, limitedOptions, id)}
            isLock={isLock}
          />
        );
      case "Mc":
        options = generateMatchOptions();

        handleSelect = (value) => {
          setHeaderStates((prev) => ({
            ...prev,
            [header]: value,
          }));

          handleFiltering("match", value);
        };

        const selectedValue =
          columnFilters.find((f) => f.id === id)?.value ?? "N";

        return (
          <SelectColumnHeader
            value={selectedValue}
            name={selectedValue === "N" ? "Mc" : ""}
            options={options}
            setValue={handleSelect}
            isLock={isLock}
            isHidden={isHidden}
            headerType="Mc"
          />
        );
      case "KM":
        options = generateKmOptions();

        handleSelect = (value) => {
          setHeaderStates((prev) => {
            return { ...prev, [header]: value };
          });
          handleFiltering("km", value);
        };
        return (
          <SelectColumnHeader
            value={columnFilters.find((f) => f.id === id)?.value ?? "N"}
            name={header}
            options={options}
            setValue={handleSelect}
            isLock={isLock}
            isHidden={isHidden}
          />
        );

      case "Bs":
        const availabilityColumn = hiddenColumns?.find((item) => item.id === "availability");
        return (
          <AvailabilityHeader
            availabilityType={availabilityType}
            setAvailabilityType={setAvailabilityType}
            columnFilters={columnFilters}
            handleFiltering={handleFiltering}
            isLock={isLock}
            isHidden={!availabilityColumn?.value}
          // isHidden={isHidden}
          />
        );
      default:
        return <>{header}</>;
    }
  };

  const [columns, setColumns] = useState([
    {
      accessorKey: "firstName",
      accessorFn: (prop) => {
        const { firstName, middleName, lastName } = prop;
        return TextLimit.Fullname(firstName, middleName, lastName);
      },
      size: 200,
      header: "Fullname",
      cell: NameCell,
      sortingFn: (rowA, rowB) => {
        const normalize = (val) => (val || "").trim().toLowerCase();

        const { lastName: lnA, firstName: fnA, middleName: mnA } = rowA.original;
        const { lastName: lnB, firstName: fnB, middleName: mnB } = rowB.original;

        const lastCompare = normalize(lnA).localeCompare(normalize(lnB));
        if (lastCompare !== 0) return lastCompare;

        const firstCompare = normalize(fnA).localeCompare(normalize(fnB));
        if (firstCompare !== 0) return firstCompare;

        return normalize(mnA).localeCompare(normalize(mnB));
      },
    },
    {
      accessorKey: "vog",
      header: "V",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const status = row.getValue(columnId);

        if (status === "locked") return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "id",
      header: "I",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const status = row.getValue(columnId);

        if (status === "locked") return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "pabo",
      header: "P",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const status = row.getValue(columnId);

        if (status === "locked") return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "d36mnd",
      header: "36",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const status = row.getValue(columnId);

        if (status === "" || status === "locked") return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "kr",
      header: "Kr",
      size: 35,
      cell: KrCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (filterStatuses.length === 0 || filterStatuses === "N") return true;

        const { ext, tot, locked, type, maxLimit, hasAbsent, hasCode } = row.getValue(columnId);

        if (locked) return true;

        if (ext == 1 && ((hasAbsent && type == 2) || (hasCode && type == 3))) {
          if (tot >= 6) {
            return false;
          }
        } else {
          if (tot >= maxLimit) {
            return false;
          }
        }
        return true;
      },
    },
    {
      accessorKey: "bk",
      header: "Bk",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const status = row.getValue(columnId);

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "gym",
      header: "Gym",
      size: 35,
      cell: IconCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (filterStatuses.length === 0 || filterStatuses === "N") return true;

        const status = row.getValue(columnId);

        if (status === "i.o.") return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "match",
      header: "Mc",
      size: 35,
      enableColumnFilter: true,
      cell: IconCell,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses?.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;
        const status = row.getValue(columnId);

        if (filterStatuses < status || status == '99') return true;

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "fv",
      header: "FV",
      size: 35,
      cell: IconCell,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses?.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;
        const status = row.getValue(columnId);

        if (filterStatuses == "+!") {
          if (status == "+" || status == "!") return true;
        }

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "fb",
      header: "FB",
      size: 35,
      cell: IconCell,
      filterFn: (row, columnId, filterStatuses) => {
        if (filterStatuses.length === 0) return true;
        const status = row.getValue(columnId);

        if (filterStatuses == "+!") {
          if (status == "+" || status == "!") return true;
        }

        return status == filterStatuses;
      },
    },
    {
      accessorKey: "con",
      header: "Con",
      size: 38,
      cell: ClassicCell,
    },
    {
      accessorKey: "km",
      header: "KM",
      size: 48,
      cell: KmCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;

        const { km, kmMax } = row.original;

        if (km === "" || km === "--") return true;
        const distance = parseInt(km);
        const maxDistance = parseInt(kmMax);
        const selectedDistance = parseInt(filterStatuses);

        if (maxDistance === null || !maxDistance) return distance <= selectedDistance;

        return (
          distance <= selectedDistance ||
          distance <= maxDistance ||
          ((distance <= selectedDistance || maxDistance >= selectedDistance) &&
            distance <= maxDistance)
        );
      },
    },
    {
      accessorKey: "age",
      header: "Lft",
      size: 38,
      cell: ClassicCell,
    },
    {
      accessorKey: "score",
      header: "S/V",
      size: 40,
      cell: ClassicCell,
    },
    {
      accessorKey: "selectedToReceiveMessage",
      size: 35,
      header: (
        <Tippy
          content={getHeaderTooltip("MESSAGES")}
          placement="top"
          arrow
          className="custom-tooltip"
          appendTo={() => document.body}
          delay={[120, 0]}
        >
          <span>
            <Button
              className="planscreen__button planscreen__button--header"
              onClick={() => setIsOpenMessageModal((prev) => !prev)}
              aria-label={getHeaderTooltip("MESSAGES")}
              type="button"
            >
              <MessageIcon fontSize={"21px"} />
            </Button>
          </span>
        </Tippy>
      ),
      cell: MessagesCell,
    },
    {
      accessorKey: "messages",
      size: 35,
      header: (
        <Tippy
          content={getHeaderTooltip("CONTACTHISTORIE")}
          placement="top"
          arrow
          className="custom-tooltip"
          appendTo={() => document.body}
          delay={[120, 0]}
        >
          <span>
            <BookIcon />
          </span>
        </Tippy>
      ),
      cell: ContactHistoryCell,
    },
    {
      accessorKey: "selected",
      size: 35,
      header: (
        <Tippy
          content={getHeaderTooltip("SELECTED")}
          placement="top"
          arrow
          className="custom-tooltip"
          appendTo={() => document.body}
          delay={[120, 0]}
        >
          <span
            role="button"
            style={{ cursor: "pointer", display: "inline-flex" }}
            onClick={() => {
              setIsActiveSelect((prev) => {
                const newValue = !prev;

                if (!newValue) {
                  setColumnFilters((prev) => prev.filter((f) => f.id !== "selected"));
                  handleUserDataUpdation(0, "selected", false);
                } else {
                  setColumnFilters((prev) => [
                    ...prev.filter((f) => f.id !== "selected"),
                    { id: "selected", value: true },
                  ]);
                }

                return newValue;
              });
            }}
          >
            <SearchIcon />
          </span>
        </Tippy>
      ),
      cell: SelectCell,
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        return value === filterValue;
      },
    }
    ,
    {
      accessorKey: "percentage",
      header: "%B",
      size: 35,
      cell: IconCell,
    },
    {
      accessorKey: "outerPercentage",
      header: "%E",
      size: 35,
      cell: IconCell,
    },
    {
      accessorKey: "availability",
      header: "Bs",
      size: 120,
      cell: AvailabilityCell,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterStatuses) => {
        if (!filterStatuses || filterStatuses.length === 0 || filterStatuses === "N" || filterStatuses === "disabled") return true;
        const status = row.getValue(columnId);
        if (!Array.isArray(status)) return false;

        if (filterStatuses === "+!") {
          return status.some((i) => i.active && (i.value === "+" || i.value === "!"));
        }

        return status.some((i) => i.active && i.value === filterStatuses);
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.getValue("availability");
        const b = rowB.getValue("availability");

        const hasPlus = (arr) =>
          Array.isArray(arr) && arr.some((i) => i.active && i.value === "+");

        const aHasPlus = hasPlus(a);
        const bHasPlus = hasPlus(b);

        if (aHasPlus && !bHasPlus) return -1;
        if (!aHasPlus && bHasPlus) return 1;

        const countActive = (arr) => (Array.isArray(arr) ? arr.filter((i) => i.active).length : 0);
        return countActive(b) - countActive(a);
      },
    },
  ]);

  const handleUserDataUpdation = (adminId, columnId, value) => {
    setData((prevData) => {
      const updated = prevData.map((item) => {
        if (adminId === 0) {
          return {
            ...item,
            [columnId]: value,
          };
        } else if (item.adminId === adminId) {
          if (columnId === 'availability') {
            // console.log('[AVAIL-DEBUG] Updating user availability:', {
            //   adminId,
            //   itemAdminId: item.adminId,
            //   match: item.adminId === adminId,
            //   oldValue: item.availability,
            //   newValue: value,
            //   newValueLength: Array.isArray(value) ? value.length : 0
            // });
          }
          return {
            ...item,
            [columnId]: value,
          };
        }

        return { ...item };
      });
      return updated;
    });
  };

  const fetchSubstitutors = () => {

    if (!contract?.id || contract.id <= 0) return [];

    return PlanscreenService.getSubstitutors(
      shiftId,
      contract.id
    ).then((res) => {
      let usersData = null;

      if (Array.isArray(res?.data)) {
        usersData = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        usersData = res.data.data;
      }

      if (!usersData) {
        throw new Error(
          "Unexpected error occurred during fetching users."
        );
      }

      return usersData.map((u) => ({
        ...u,
        adminId: Number(u.adminId),
        isLoadingAvailability: false,
      }));
    });
  };

  const {
    data: substitutorsData,
    error: substitutorsError,
    isLoading,
  } = useSWR(
    contract?.id > 0
      ? ["substitutors", shiftId, contract.id]
      : null,
    fetchSubstitutors
  );
  useEffect(() => {
    if (!substitutorsData) return;

    setData(substitutorsData);

    const assignedUsersArray = substitutorsData
      .filter((u) => u.isAssigned === true)
      .map((u) => ({ ...u, sourceContractId: contract.id }));

    if (assignedUsersArray.length > 0) {
      setAssignedUsers((prev) => {
        const usersToAdd = assignedUsersArray.filter(
          (newUser) => {
            console.log("[DEBUG] Substitutors data loaded:", prev, newUser);
            return !prev.some(
              (existing) =>
                existing.adminId === newUser.adminId
            )
          }
        );

        return [...prev, ...usersToAdd];
      });
    }

  }, [substitutorsData]);

  useEffect(() => {
    if (!substitutorsError) return;

    const errorMessage =
      substitutorsError?.response?.data?.message ||
      substitutorsError?.message ||
      "Unknown error";

    const errorStatus =
      substitutorsError?.response?.status || "N/A";

    setError(
      `Failed to load substitutors (Status: ${errorStatus}): ${errorMessage}`
    );
  }, [substitutorsError]);


  const table = useReactTable({
    data: data,
    columns,
    state: {
      columnFilters,
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      contract,
      hiddenColumns,
      availabilityType,
      usersToNotify,
      updateRecipientsArray: (user) => {
        setUsersToNotify((prev) => {
          const userExists = prev.some((u) => u.adminId === user.adminId);
          return userExists
            ? prev.filter((u) => u.adminId !== user.adminId)
            : [...prev, user];
        });
      },
      updateUserData: handleUserDataUpdation,
    },
  });

  useEffect(() => {
    if (contract.id < 0) {
      setData(assignedUsers);
    }
  }, [assignedUsers, contract]);

  useEffect(() => {
    if (!Array.isArray(filtersData)) return;
    const normalized = normalizeFilters(filtersData);
    setFilters(normalized);
    if (!isIngepland && communityContractKey) {
      setCachedFiltersForContract(normalizedCommunityId, normalizedContractId, normalized);
    }
  }, [filtersData, communityContractKey]);

  useEffect(() => {
    switch (filterSortBy) {
      case 2:
        setSorting([
          { id: "km", desc: false },
          { id: "firstName", desc: false },
          { id: "percentage", desc: true },
          { id: "outerPercentage", desc: true },
        ]);
        break;
      case 3:
        setSorting([
          { id: "age", desc: false },
          { id: "firstName", desc: false },
          { id: "percentage", desc: true },
          { id: "outerPercentage", desc: true },
        ]);
        break;
      case 4:
        setSorting([
          { id: "percentage", desc: true },
          { id: "outerPercentage", desc: true },
          { id: "firstName", desc: false },
          { id: "availability", desc: false },
        ]);
        break;
      default:
        setSorting([
          { id: "firstName", desc: false },
          { id: "percentage", desc: true },
          { id: "outerPercentage", desc: true },
        ]);
        break;
    }
  }, [filterSortBy]);

  useEffect(() => {
    if (filters.length === 0) return;

    let selectedFilter = filters.find((filter) => filter.isSelected);

    if (!selectedFilter && filters.length > 0) {
      selectedFilter = filters[0];
      setFilters(prev => prev.map((f, i) => ({ ...f, isSelected: i === 0 })));
      return;
    }

    if (!selectedFilter) return;

    const newLockFilters = [];
    const newHiddenColumns = [];
    const newColumnFilters = [];
    const newColumnVisibility = {};


    Object.entries(selectedFilter).forEach(([key, value]) => {
      if (key.includes("Lock") && value) {
        const lockKey = key.replace("Lock", "");
        newLockFilters.push({ id: lockKey, value });
      }

      if (key.includes("Ignore") && value) {
        const ignoreKey = key.replace("Ignore", "");

        if (ignoreKey === "sms") {
          newHiddenColumns.push({ id: "selectedToReceiveMessage", value });
        } else {
          newHiddenColumns.push({ id: ignoreKey, value });
        }
      }
      if (!isIngepland && key.includes("Value") && value !== "N" && value !== "disabled") {
        // const valueKey = key.replace("Value", "");

        // const ignoreKey = `${valueKey}Ignore`;
        // //console.log('ignoreKey:', ignoreKey , 'selectedFilter[ignoreKey]:', selectedFilter[ignoreKey], 'value:', value);
        // if (!selectedFilter[ignoreKey] && value !== "" && value !== "disabled") {
        //   newColumnFilters.push({ id: valueKey, value });
        // }

        const valueKey = key.replace("Value", "");
        const ignoreKey = `${valueKey}Ignore`;

        if (
          !selectedFilter[ignoreKey] &&
          value !== "" &&
          value !== "disabled"
        ) {
          newColumnFilters.push({
            id: valueKey,
            value,
          });
        }
      }

      if (key.endsWith("Visible")) {
        let visibilityKey = key.replace("Visible", "");
        if (visibilityKey === "sms") visibilityKey = "selectedToReceiveMessage";
        newColumnVisibility[visibilityKey] = !value;
      }
    });


    setLockFilters((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newLockFilters)) {
        return newLockFilters;
      }
      return prev;
    });
    setHiddenColumns((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newHiddenColumns)) {
        return newHiddenColumns;
      }
      return prev;
    });
    setColumnFilters((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(newColumnFilters)) {
        return newColumnFilters;
      }
      return prev;
    });
    setColumnVisibility((prev) =>
      JSON.stringify(prev) !== JSON.stringify(newColumnVisibility)
        ? newColumnVisibility
        : prev
    );

    if (!isIngepland) {
      registerContractView?.(contract.id, {
        hiddenColumns: newHiddenColumns,
        visibility: newColumnVisibility,
      });
    }
    setFilterSortBy(selectedFilter?.sort ?? sortBy ?? 1);
    setAvailabilityType(selectedFilter?.availabilityType ?? "dagdelen");
  }, [filters]);

  useEffect(() => {
    if (usersToNotify.length < 1) handleUserDataUpdation(0, "selectedToReceiveMessage", false);
  }, [usersToNotify]);

  useEffect(() => {
    setFilterSortBy(sortBy);
  }, [sortBy]);

  const NO_ROW_CLICK = 'a, button, input, select, textarea, label, [role="button"], .no-row-click';

  const handleRowClick = (e) => {
    // ignore clicks on controls inside the row 
    if (e.target.closest(NO_ROW_CLICK)) return;
    // ignore when the user is selecting text
    if (window.getSelection()?.toString()) return;

    // trigger the same action the name cell already has
    const nameTarget = e.currentTarget.querySelector(
      'td:first-child a, td:first-child button, td:first-child [role="button"], td:first-child .cursor-pointer'
    );
    nameTarget?.click();
  };

 useEffect(() => {
  if (isLoading || error) return;
  // Ingepland (contract.id < 0) is fed from assignedUsers, so don't report it
  if (contract.id < 0) return;
  // wait until substitutorsData has actually been copied into `data`
  if (!substitutorsData) return;
  onEmptyChange?.(contract.id, substitutorsData.length === 0);
}, [isLoading, error, substitutorsData, contract.id, onEmptyChange]);

  return (
    <div className="planscreen__table-wrapper">
      {isLoading && data.length === 0 && (
        <Loader
          type="ball-grid-pulse"
          className={"mt-3 d-flex mx-auto justify-content-center"}
        />
      )}
      {!error && data && data.length > 0 && (
        <div className="planscreen__table-container">

          <table className="table base-table planscreen__table">
            <thead>
              {table.getHeaderGroups()?.map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup?.headers?.map((header, index) => {
                    const isLastHeader = index === headerGroup.headers.length - 1;
                    return (
                      <th
                        key={header.id} className={`planscreen__th border-b ${header.id === "match" ? "p-0 text-center gap-0" : "py-2"
                          } ${index === 0 ? "border-end planscreen__th--first" : ""
                          }`}
                        // className={`py-2 planscreen__th border-b ${index === 0 ? "border-end planscreen__th--first" : ""
                        //   }`}
                        style={{
                          ...(index === 0 ? {} : {
                            width: header.column.columnDef.size,
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }),
                          ...(isLastHeader ? {
                            width: '400px',
                            minWidth: '400px',
                            maxWidth: '400px'
                          } : {})
                        }}
                      >
                        <div
                          className={`d-flex ${header.id !== "firstName" && header.id !== "availability"
                            ? "justify-content-center"
                            : ""
                            }`}
                        >
                          {renderHeaderCell(header.column.columnDef.header, header.id)}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <tr key={row.id} onClick={handleRowClick} className="planscreen__row">
                  {row?.getVisibleCells().map((cell, i) => {
                    const colId = cell.column.id;
                    const isLastCell = i === row.getVisibleCells().length - 1;
                    return (
                      <td
                        key={cell.id}
                        className={`py-0 planscreen__th
                ${i === 0 ? "border-end planscreen__th--first" : ""}
                ${index % 2 === 1
                            ? "planscreen__th--odd"
                            : "planscreen__th--even"
                          }`}
                        style={{
                          ...(i === 0 ? {} : {
                            width: cell.column.columnDef.size,
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }),
                          ...(isLastCell ? { border: 0, padding: 0 } : {})
                        }}
                      >
                        <div
                          className={`d-flex ${colId !== "firstName" && colId !== "availability"
                            ? "justify-content-center"
                            : ""
                            }`}
                        >
                          {(() => {
                            const ctx = cell.getContext();
                            if (!isIngepland) return flexRender(cell.column.columnDef.cell, ctx);

                            const view =
                              contractViews?.[row.original.sourceContractId] ??
                              { hiddenColumns: [], visibility: {} };
                            if (view.visibility[colId] === false) return null;

                            return flexRender(cell.column.columnDef.cell, {
                              ...ctx,
                              table: {
                                ...ctx.table,
                                options: {
                                  ...ctx.table.options,
                                  meta: { ...ctx.table.options.meta, hiddenColumns: view.hiddenColumns },
                                },
                              },
                            });
                          })()}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && error && <div className="text-danger">{error}</div>}
    </div>
  );
}